/**
 * EAA-1736: Media elements for the FITB/drag-the-words/dropdown passage player.
 *
 * The passage player builds its own DOM (to splice in blank widgets) and used to
 * render a raw <img> with the stored src while dropping audio/embed entirely — so
 * storage-backed images never loaded and audio/video was invisible in the player.
 * These components resolve storage srcs (like the editor NodeViews) and honor the
 * shared media layout (inline / wrap / behind / front).
 */

import * as React from 'react';
import { Pause, Play } from 'lucide-react';
import {
    readMediaLayoutState,
    mediaLayoutInlineStyle,
    Button,
} from '@ai-content-hub/content-kit';
import {
    resolveMediaUrlForViewer,
    shouldResolveMediaUrlForViewer,
} from '@ai-content-hub/content-core';
import { DEFAULT_IMAGE_MAX_HEIGHT, IMAGE_MAX_HEIGHT_VAR } from '../image/imageStyle';

/** Attrs read off a passage media node. */
export interface PassageMediaAttrs {
    src?: string;
    alt?: string;
    title?: string;
    width?: number | string | null;
    height?: number | null;
    layout?: string;
    x?: number | null;
    y?: number | null;
    /** EAA-1736: 'full' = native <audio controls>, 'minimal' = play button only. */
    style?: string;
}

/** Resolve a storage-path media src to a viewable URL (editor parity). */
function useResolvedMediaUrl(src: string): string {
    const [url, setUrl] = React.useState<string>(() =>
        src && !shouldResolveMediaUrlForViewer(src) ? src : ''
    );

    React.useEffect(() => {
        if (
            !src ||
            src.startsWith('data:') ||
            src.startsWith('blob:') ||
            !shouldResolveMediaUrlForViewer(src)
        ) {
            setUrl(src);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const resolved = await resolveMediaUrlForViewer(src);
                if (!cancelled) setUrl(resolved || src);
            } catch {
                if (!cancelled) setUrl(src);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [src]);

    return url;
}

function layoutStyleFor(attrs: PassageMediaAttrs): React.CSSProperties {
    const state = readMediaLayoutState(attrs as Record<string, unknown>);
    return mediaLayoutInlineStyle(state);
}

export const PassageImage: React.FC<{ attrs: PassageMediaAttrs }> = ({ attrs }) => {
    const resolved = useResolvedMediaUrl(String(attrs.src ?? ''));
    if (!resolved) return null;
    const layoutStyle = layoutStyleFor(attrs);
    return (
        <img
            src={resolved}
            alt={String(attrs.alt ?? '')}
            data-layout={attrs.layout}
            style={{
                ...layoutStyle,
                // EAA-2063: a block picture sits centred, like a page image; inline and
                // wrapped pictures keep the margins their layout sets.
                ...(layoutStyle.display === 'block' ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
                width: attrs.width ? `${attrs.width}px` : undefined,
                // EAA-2063: a passage picture (dropdown / fill-in / drag-the-words) was
                // drawn at its full authored width with no height limit, pushing the
                // sentence off screen. Same cap as page images; `contain` keeps the aspect
                // ratio when the authored width meets the height cap.
                maxHeight: `var(${IMAGE_MAX_HEIGHT_VAR}, ${DEFAULT_IMAGE_MAX_HEIGHT})`,
                objectFit: 'contain',
            }}
            className="fitb-passage-img max-w-full h-auto rounded-md my-2"
        />
    );
};

export const PassageAudio: React.FC<{ attrs: PassageMediaAttrs }> = ({ attrs }) => {
    const resolved = useResolvedMediaUrl(String(attrs.src ?? ''));
    const audioRef = React.useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = React.useState(false);

    if (!resolved) return null;

    // EAA-1736: "minimal" style — a play/pause button instead of the native
    // controls bar, mirroring the editor's AudioView minimal branch.
    if (attrs.style === 'minimal') {
        const handleToggle = () => {
            const el = audioRef.current;
            if (!el) return;
            if (el.paused) void el.play();
            else el.pause();
        };
        return (
            <span
                className="fitb-passage-audio-minimal inline-flex items-center gap-2 align-middle"
                data-layout={attrs.layout}
                style={layoutStyleFor(attrs)}
            >
                <audio
                    ref={audioRef}
                    src={resolved}
                    title={attrs.title ? String(attrs.title) : undefined}
                    preload="metadata"
                    className="fitb-passage-audio hidden"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                />
                <Button
                    type="button"
                    variant="default"
                    size="icon"
                    className="fitb-passage-audio-minimal-btn"
                    onClick={handleToggle}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? <Pause /> : <Play />}
                </Button>
            </span>
        );
    }

    return (
        <audio
            src={resolved}
            title={attrs.title ? String(attrs.title) : undefined}
            controls
            preload="metadata"
            data-layout={attrs.layout}
            style={layoutStyleFor(attrs)}
            className="fitb-passage-audio block w-full max-w-[480px] my-2 rounded-md"
        />
    );
};

export const PassageEmbed: React.FC<{ attrs: PassageMediaAttrs }> = ({ attrs }) => {
    const src = String(attrs.src ?? '');
    if (!src) return null;
    return (
        <iframe
            src={src}
            title={attrs.title ? String(attrs.title) : 'Embedded content'}
            width={attrs.width ? String(attrs.width) : '100%'}
            height={attrs.height ?? 315}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            referrerPolicy="no-referrer-when-downgrade"
            loading="lazy"
            data-layout={attrs.layout}
            style={layoutStyleFor(attrs)}
            className="fitb-passage-embed block border-0 rounded-md max-w-full my-2"
        />
    );
};
